import { useEffect, useState } from "react";
import { systemApi } from "../api/services";
import { useAuth } from "../App";
import { T } from "../tokens";

const KIND_STYLE = {
  info:     { color: "#7AB8E8", bg: "rgba(122,184,232,.1)",  border: "rgba(122,184,232,.4)", icon: "ℹ" },
  promo:    { color: "#facc15", bg: "rgba(250,204,21,.1)",   border: "rgba(250,204,21,.4)", icon: "🎉" },
  warning:  { color: "#fb923c", bg: "rgba(251,146,60,.1)",   border: "rgba(251,146,60,.4)", icon: "⚠" },
  critical: { color: "#f87171", bg: "rgba(248,113,113,.1)",  border: "rgba(248,113,113,.4)", icon: "🚨" },
};

const DISMISS_PREFIX = "fyz_banner_dismissed_";

/**
 * Public + role-aware site banners. Polls every 5 minutes — admins set
 * banners infrequently and we don't want to flood the API.
 *
 * Each banner can be dismissed locally; dismissals persist in localStorage
 * keyed by banner id so the same one doesn't reappear on every nav.
 */
export default function SiteBanners() {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => loadDismissed());

  useEffect(() => {
    let mounted = true;
    let interval;

    const fetch = async () => {
      try {
        const fn = user ? systemApi.myBanners : systemApi.banners;
        const res = await fn();
        if (mounted) setBanners(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch {
        // Banner outage shouldn't be visible to users.
      }
    };
    fetch();
    interval = setInterval(fetch, 5 * 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, [user?.id]);

  const visible = banners.filter((b) => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {visible.map((b) => {
        const k = KIND_STYLE[b.kind] || KIND_STYLE.info;
        return (
          <div key={b.id} style={{
            background: k.bg,
            borderBottom: `1px solid ${k.border}`,
            color: k.color,
            padding: "9px 24px",
            fontSize: 13,
            fontFamily: "'DM Sans',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
          }}>
            <span style={{ fontSize: 14 }}>{k.icon}</span>
            <span style={{ color: T.text, flex: "0 1 auto", textAlign: "center" }}>
              {b.message}
              {b.link_url && (
                <>
                  {" "}
                  <a href={b.link_url} target="_blank" rel="noopener noreferrer" style={{ color: k.color, fontWeight: 600, textDecoration: "underline" }}>
                    {b.link_text || "Learn more"} →
                  </a>
                </>
              )}
            </span>
            <button
              onClick={() => dismiss(b.id)}
              aria-label="Dismiss banner"
              style={{ marginLeft: 8, padding: "2px 8px", background: "transparent", border: "none", color: k.color, fontSize: 16, cursor: "pointer", fontFamily: "inherit", lineHeight: 1, opacity: 0.7 }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

function loadDismissed() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(DISMISS_PREFIX + "ids");
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  try {
    localStorage.setItem(DISMISS_PREFIX + "ids", JSON.stringify([...set]));
  } catch { /* quota errors are fine */ }
}
